import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, UserPlus, Heart, MapPin, Megaphone, HandHeart,
  TrendingUp, Calendar, Mail, Phone, Search, Filter,
  ChevronDown, ChevronRight, Eye, CheckCircle, Clock,
  XCircle, Download, RefreshCw, Lock, LogOut, BarChart3,
  Wallet, Globe, Smartphone, AlertCircle, CheckCircle2, Ban
} from 'lucide-react';

const AdminPage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  
  // Data state
  const [stats, setStats] = useState(null);
  const [donationStats, setDonationStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [supporters, setSupporters] = useState([]);
  const [mobileMoneyTransactions, setMobileMoneyTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Filters
  const [pathFilter, setPathFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [mmStatusFilter, setMmStatusFilter] = useState('all');
  const [mmProviderFilter, setMmProviderFilter] = useState('all');
  
  // Selected member for detail view
  const [selectedMember, setSelectedMember] = useState(null);
  
  // Confirmation modal
  const [confirmingTransaction, setConfirmingTransaction] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Admin password (in production, this should be server-side)
  const ADMIN_PASSWORD = 'ada2024admin';

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      // Verify password with backend
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Password': password
        }
      });
      
      if (response.ok) {
        setIsAuthenticated(true);
        setAuthError('');
        // Store password in sessionStorage (not localStorage for security)
        sessionStorage.setItem('ada_admin_password', password);
      } else {
        const errorData = await response.json();
        setAuthError(errorData.detail || 'Mot de passe incorrect');
      }
    } catch (error) {
      // Fallback to frontend validation if backend unavailable
      if (password === ADMIN_PASSWORD) {
        setIsAuthenticated(true);
        setAuthError('');
        sessionStorage.setItem('ada_admin_password', password);
      } else {
        setAuthError('Mot de passe incorrect');
      }
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('ada_admin_password');
  };

  // Get stored password for API calls
  const getAdminPassword = () => {
    return sessionStorage.getItem('ada_admin_password') || ADMIN_PASSWORD;
  };

  // Check if already authenticated
  useEffect(() => {
    const storedPassword = sessionStorage.getItem('ada_admin_password');
    if (storedPassword) {
      setPassword(storedPassword);
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    }
  }, [isAuthenticated]);

  const fetchAllData = async () => {
    setLoading(true);
    const adminPassword = getAdminPassword();
    const headers = {
      'Content-Type': 'application/json',
      'X-Admin-Password': adminPassword
    };
    
    try {
      const [statsRes, membersRes, supportersRes, mmRes, donationStatsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers }),
        fetch(`${API_URL}/api/admin/members`, { headers }),
        fetch(`${API_URL}/api/admin/supporters`, { headers }),
        fetch(`${API_URL}/api/admin/donations/mobile-money`, { headers }),
        fetch(`${API_URL}/api/admin/donations/stats`, { headers })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (membersRes.ok) setMembers(await membersRes.json());
      if (supportersRes.ok) setSupporters(await supportersRes.json());
      if (mmRes.ok) setMobileMoneyTransactions(await mmRes.json());
      if (donationStatsRes.ok) setDonationStats(await donationStatsRes.json());
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    }
    setLoading(false);
  };

  const confirmMobileMoneyPayment = async (reference) => {
    const adminPassword = getAdminPassword();
    try {
      const response = await fetch(`${API_URL}/api/admin/donations/mobile-money/confirm/${reference}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Admin-Password': adminPassword
        }
      });
      
      if (response.ok) {
        // Update local state
        setMobileMoneyTransactions(prev => prev.map(t => 
          t.reference === reference ? { ...t, status: 'completed' } : t
        ));
        setConfirmingTransaction(null);
      }
    } catch (error) {
      console.error('Failed to confirm payment:', error);
    }
  };

  const updateMemberStatus = async (memberId, newStatus) => {
    const adminPassword = getAdminPassword();
    try {
      const response = await fetch(`${API_URL}/api/admin/members/${memberId}/status`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          'X-Admin-Password': adminPassword
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        setMembers(prev => prev.map(m => 
          m.id === memberId ? { ...m, status: newStatus } : m
        ));
        if (selectedMember?.id === memberId) {
          setSelectedMember(prev => ({ ...prev, status: newStatus }));
        }
      }
    } catch (error) {
      console.error('Failed to update member status:', error);
    }
  };

  // Export functions - need to include password in URL or use POST
  const exportMembersCSV = async () => {
    const adminPassword = getAdminPassword();
    try {
      const response = await fetch(`${API_URL}/api/admin/export/members`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ada_membres_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const exportSupportersCSV = async () => {
    const adminPassword = getAdminPassword();
    try {
      const response = await fetch(`${API_URL}/api/admin/export/supporters`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ada_supporters_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const exportMobileMoneyCSV = async () => {
    const adminPassword = getAdminPassword();
    try {
      const response = await fetch(`${API_URL}/api/admin/export/mobile-money`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ada_mobile_money_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  // Filter members
  const filteredMembers = members.filter(member => {
    const matchesPath = pathFilter === 'all' || member.path === pathFilter;
    const matchesStatus = statusFilter === 'all' || member.status === statusFilter;
    const matchesSearch = searchQuery === '' || 
      `${member.firstName} ${member.lastName} ${member.email} ${member.city}`.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesPath && matchesStatus && matchesSearch;
  });

  const pathLabels = {
    volunteer: { label: 'Bénévole', icon: HandHeart, color: '#5B2D8E' },
    circleCreator: { label: 'Créateur de cercle', icon: MapPin, color: '#0D9488' },
    ambassador: { label: 'Ambassadeur', icon: Megaphone, color: '#F7C948' },
    supporter: { label: 'Sympathisant', icon: Heart, color: '#E11D48' }
  };

  const statusLabels = {
    pending: { label: 'En attente', color: '#F59E0B', icon: Clock },
    approved: { label: 'Approuvé', color: '#10B981', icon: CheckCircle },
    active: { label: 'Actif', color: '#3B82F6', icon: CheckCircle },
    rejected: { label: 'Refusé', color: '#EF4444', icon: XCircle }
  };

  // Login form
  if (!isAuthenticated) {
    return (
      <div className={`min-h-screen pt-20 flex items-center justify-center ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`w-full max-w-md p-8 rounded-xl ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'} shadow-xl`}
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#5B2D8E] flex items-center justify-center">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Administration ADA
            </h1>
            <p className={`mt-2 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
              Accès réservé aux administrateurs
            </p>
          </div>

          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                Mot de passe
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full px-4 py-3 rounded-lg border transition-colors ${
                  isDark 
                    ? 'bg-white/5 border-white/10 text-white focus:border-[#F7C948]' 
                    : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-[#5B2D8E]'
                } outline-none`}
                placeholder="••••••••"
              />
            </div>

            {authError && (
              <p className="text-red-500 text-sm mb-4">{authError}</p>
            )}

            <button
              type="submit"
              className="w-full py-3 bg-[#5B2D8E] text-white font-bold rounded-lg hover:bg-[#4A2472] transition-colors"
            >
              Connexion
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
      {/* Header */}
      <div className={`border-b ${isDark ? 'bg-[#1A0F2E] border-white/10' : 'bg-white border-gray-200'}`}>
        <div className="container mx-auto px-4 lg:px-10 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#5B2D8E] flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Dashboard Administration
                </h1>
                <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  Alliance Démocratique Africaine
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <button
                onClick={fetchAllData}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? 'hover:bg-white/10 text-white/70' : 'hover:bg-gray-100 text-gray-600'
                }`}
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={handleLogout}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  isDark ? 'hover:bg-white/10 text-white/70' : 'hover:bg-gray-100 text-gray-600'
                }`}
              >
                <LogOut className="w-4 h-4" />
                Déconnexion
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 overflow-x-auto">
            {[
              { id: 'overview', label: 'Vue d\'ensemble', icon: BarChart3 },
              { id: 'members', label: 'Membres', icon: Users },
              { id: 'supporters', label: 'Supporters 4ᵉ', icon: Heart },
              { id: 'mobile-money', label: 'Mobile Money', icon: Smartphone, badge: mobileMoneyTransactions.filter(t => t.status === 'pending').length }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-[#5B2D8E] text-white'
                    : isDark ? 'text-white/60 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.badge > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-xs font-bold rounded-full bg-[#F7C948] text-[#0A0510]">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 lg:px-10 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin text-[#5B2D8E]" />
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && stats && (
              <div className="space-y-8">
                {/* Stats Cards */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-6 rounded-xl ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'} shadow-lg`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-[#5B2D8E]/20 flex items-center justify-center">
                        <Users className="w-6 h-6 text-[#5B2D8E]" />
                      </div>
                      <span className="text-[#0D9488] text-sm font-medium flex items-center gap-1">
                        <TrendingUp className="w-4 h-4" />
                        +{stats.new_this_week || 0} cette semaine
                      </span>
                    </div>
                    <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {stats.total_members}
                    </p>
                    <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                      Membres inscrits
                    </p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className={`p-6 rounded-xl ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'} shadow-lg`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-[#F7C948]/20 flex items-center justify-center">
                        <Heart className="w-6 h-6 text-[#F7C948]" />
                      </div>
                    </div>
                    <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {stats.total_supporters}
                    </p>
                    <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                      Supporters 4ᵉ République
                    </p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className={`p-6 rounded-xl ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'} shadow-lg`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-[#0D9488]/20 flex items-center justify-center">
                        <Clock className="w-6 h-6 text-[#0D9488]" />
                      </div>
                    </div>
                    <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {stats.pending_members}
                    </p>
                    <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                      En attente d&apos;approbation
                    </p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className={`p-6 rounded-xl ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'} shadow-lg`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-[#E11D48]/20 flex items-center justify-center">
                        <Mail className="w-6 h-6 text-[#E11D48]" />
                      </div>
                    </div>
                    <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {stats.newsletter_subscribers}
                    </p>
                    <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                      Abonnés newsletter
                    </p>
                  </motion.div>
                </div>

                {/* Members by Path */}
                <div className={`p-6 rounded-xl ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'} shadow-lg`}>
                  <h3 className={`text-lg font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Répartition par parcours
                  </h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Object.entries(pathLabels).map(([key, { label, icon: Icon, color }]) => (
                      <div
                        key={key}
                        className={`p-4 rounded-lg border ${isDark ? 'border-white/10' : 'border-gray-200'}`}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${color}20` }}
                          >
                            <Icon className="w-5 h-5" style={{ color }} />
                          </div>
                          <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {label}
                          </span>
                        </div>
                        <p className="text-2xl font-bold" style={{ color }}>
                          {stats.by_path?.[key] || 0}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Donation Statistics */}
                {donationStats && (
                  <div className={`p-6 rounded-xl ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'} shadow-lg`}>
                    <div className="flex items-center justify-between mb-6">
                      <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Statistiques des dons
                      </h3>
                      <button
                        onClick={() => setActiveTab('mobile-money')}
                        className="text-[#0D9488] text-sm font-medium flex items-center gap-1 hover:underline"
                      >
                        Voir les transactions
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      {/* Total Combined */}
                      <div className={`p-4 rounded-lg border-2 border-[#0D9488] ${isDark ? 'bg-[#0D9488]/10' : 'bg-[#0D9488]/5'}`}>
                        <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>Montant total</p>
                        <p className="text-2xl font-bold text-[#0D9488]">
                          {new Intl.NumberFormat('fr-FR').format(donationStats.combined?.total_amount || 0)}
                          <span className="text-sm font-normal ml-1">FCFA</span>
                        </p>
                      </div>
                      
                      {/* Total Transactions */}
                      <div className={`p-4 rounded-lg border ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                        <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>Transactions totales</p>
                        <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {donationStats.combined?.total_transactions || 0}
                        </p>
                      </div>
                      
                      {/* Stripe */}
                      <div className={`p-4 rounded-lg border ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Wallet className="w-4 h-4 text-[#5B2D8E]" />
                          <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>Carte bancaire</p>
                        </div>
                        <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {new Intl.NumberFormat('fr-FR').format(donationStats.stripe?.total_amount || 0)}
                          <span className="text-xs font-normal ml-1">FCFA</span>
                        </p>
                        <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                          {donationStats.stripe?.completed || 0} transactions
                        </p>
                      </div>
                      
                      {/* Mobile Money */}
                      <div className={`p-4 rounded-lg border ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Smartphone className="w-4 h-4 text-[#F7C948]" />
                          <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>Mobile Money</p>
                        </div>
                        <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {new Intl.NumberFormat('fr-FR').format(donationStats.mobile_money?.total_amount || 0)}
                          <span className="text-xs font-normal ml-1">FCFA</span>
                        </p>
                        <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                          {donationStats.mobile_money?.completed || 0} confirmées / {donationStats.mobile_money?.pending || 0} en attente
                        </p>
                      </div>
                    </div>
                    
                    {/* Mobile Money by Provider */}
                    {donationStats.mobile_money?.by_provider && (
                      <div className={`p-4 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                        <p className={`text-sm font-medium mb-3 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                          Répartition Mobile Money par opérateur
                        </p>
                        <div className="flex flex-wrap gap-3">
                          {[
                            { id: 'orange', name: 'Orange', color: '#FF6600' },
                            { id: 'mtn', name: 'MTN', color: '#FFCC00' },
                            { id: 'wave', name: 'Wave', color: '#1DC3F5' },
                            { id: 'moov', name: 'Moov', color: '#0066CC' }
                          ].map(provider => (
                            <div 
                              key={provider.id}
                              className="flex items-center gap-2 px-3 py-1.5 rounded-full"
                              style={{ backgroundColor: `${provider.color}20` }}
                            >
                              <div 
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: provider.color }}
                              />
                              <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>
                                {provider.name}
                              </span>
                              <span 
                                className="text-sm font-bold"
                                style={{ color: provider.color }}
                              >
                                {donationStats.mobile_money.by_provider[provider.id] || 0}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Recent Members */}
                <div className={`p-6 rounded-xl ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'} shadow-lg`}>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Dernières inscriptions
                    </h3>
                    <button
                      onClick={() => setActiveTab('members')}
                      className="text-[#5B2D8E] text-sm font-medium flex items-center gap-1 hover:underline"
                    >
                      Voir tout
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-3">
                    {members.slice(0, 5).map(member => {
                      const pathInfo = pathLabels[member.path] || pathLabels.supporter;
                      const PathIcon = pathInfo.icon;
                      return (
                        <div
                          key={member.id}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            isDark ? 'bg-white/5' : 'bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                              style={{ backgroundColor: pathInfo.color }}
                            >
                              {member.firstName?.[0]}{member.lastName?.[0]}
                            </div>
                            <div>
                              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {member.firstName} {member.lastName}
                              </p>
                              <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                                {member.city} • {pathInfo.label}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span 
                              className="px-2 py-1 rounded-full text-xs font-medium"
                              style={{ 
                                backgroundColor: `${statusLabels[member.status]?.color}20`,
                                color: statusLabels[member.status]?.color
                              }}
                            >
                              {statusLabels[member.status]?.label}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Members Tab */}
            {activeTab === 'members' && (
              <div className="space-y-6">
                {/* Filters */}
                <div className={`p-4 rounded-xl ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'} shadow-lg`}>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex-1 min-w-[200px]">
                      <div className="relative">
                        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
                        <input
                          type="text"
                          placeholder="Rechercher..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                            isDark 
                              ? 'bg-white/5 border-white/10 text-white' 
                              : 'bg-gray-50 border-gray-200 text-gray-900'
                          } outline-none`}
                        />
                      </div>
                    </div>
                    
                    <select
                      value={pathFilter}
                      onChange={(e) => setPathFilter(e.target.value)}
                      className={`px-4 py-2 rounded-lg border ${
                        isDark 
                          ? 'bg-white/5 border-white/10 text-white' 
                          : 'bg-gray-50 border-gray-200 text-gray-900'
                      } outline-none`}
                    >
                      <option value="all">Tous les parcours</option>
                      {Object.entries(pathLabels).map(([key, { label }]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>

                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className={`px-4 py-2 rounded-lg border ${
                        isDark 
                          ? 'bg-white/5 border-white/10 text-white' 
                          : 'bg-gray-50 border-gray-200 text-gray-900'
                      } outline-none`}
                    >
                      <option value="all">Tous les statuts</option>
                      {Object.entries(statusLabels).map(([key, { label }]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>

                    <span className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                      {filteredMembers.length} membre(s)
                    </span>

                    <button
                      onClick={exportMembersCSV}
                      className="flex items-center gap-2 px-4 py-2 bg-[#0D9488] text-white text-sm font-medium rounded-lg hover:bg-[#0b7c72] transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Exporter CSV
                    </button>
                  </div>
                </div>

                {/* Members List */}
                <div className={`rounded-xl overflow-hidden ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'} shadow-lg`}>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className={isDark ? 'bg-white/5' : 'bg-gray-50'}>
                          <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                            Membre
                          </th>
                          <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                            Parcours
                          </th>
                          <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                            Ville
                          </th>
                          <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                            Statut
                          </th>
                          <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                            Date
                          </th>
                          <th className={`px-4 py-3 text-right text-sm font-semibold ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredMembers.map((member, index) => {
                          const pathInfo = pathLabels[member.path] || pathLabels.supporter;
                          const PathIcon = pathInfo.icon;
                          const statusInfo = statusLabels[member.status] || statusLabels.pending;
                          
                          return (
                            <tr 
                              key={member.id}
                              className={`border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`}
                            >
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <div 
                                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                                    style={{ backgroundColor: pathInfo.color }}
                                  >
                                    {member.firstName?.[0]}{member.lastName?.[0]}
                                  </div>
                                  <div>
                                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                      {member.firstName} {member.lastName}
                                    </p>
                                    <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                                      {member.email}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <PathIcon className="w-4 h-4" style={{ color: pathInfo.color }} />
                                  <span className={`text-sm ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                                    {pathInfo.label}
                                  </span>
                                </div>
                              </td>
                              <td className={`px-4 py-3 text-sm ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                                {member.city}
                              </td>
                              <td className="px-4 py-3">
                                <select
                                  value={member.status}
                                  onChange={(e) => updateMemberStatus(member.id, e.target.value)}
                                  className={`px-2 py-1 rounded-lg text-sm border-0 ${
                                    isDark ? 'bg-white/10' : 'bg-gray-100'
                                  }`}
                                  style={{ color: statusInfo.color }}
                                >
                                  {Object.entries(statusLabels).map(([key, { label }]) => (
                                    <option key={key} value={key}>{label}</option>
                                  ))}
                                </select>
                              </td>
                              <td className={`px-4 py-3 text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                                {new Date(member.created_at).toLocaleDateString('fr-FR')}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  onClick={() => setSelectedMember(member)}
                                  className={`p-2 rounded-lg transition-colors ${
                                    isDark ? 'hover:bg-white/10 text-white/70' : 'hover:bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Supporters Tab */}
            {activeTab === 'supporters' && (
              <div className={`rounded-xl overflow-hidden ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'} shadow-lg`}>
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                  <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Supporters de la 4ᵉ République ({supporters.length})
                  </h3>
                  <button
                    onClick={exportSupportersCSV}
                    className="flex items-center gap-2 px-4 py-2 bg-[#F7C948] text-[#0A0510] text-sm font-medium rounded-lg hover:bg-[#E5A700] transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Exporter CSV
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={isDark ? 'bg-white/5' : 'bg-gray-50'}>
                        <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                          Nom
                        </th>
                        <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                          Email
                        </th>
                        <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                          Ville
                        </th>
                        <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                          Source
                        </th>
                        <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                          Date
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {supporters.map((supporter, index) => (
                        <tr 
                          key={supporter.id || index}
                          className={`border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`}
                        >
                          <td className={`px-4 py-3 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {supporter.first_name} {supporter.last_name}
                          </td>
                          <td className={`px-4 py-3 text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                            {supporter.email}
                          </td>
                          <td className={`px-4 py-3 text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                            {supporter.city || '-'}
                          </td>
                          <td className={`px-4 py-3 text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                            {supporter.source === '4e_republique' ? 'Page d\'accueil' : supporter.source || '-'}
                          </td>
                          <td className={`px-4 py-3 text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                            {new Date(supporter.created_at).toLocaleDateString('fr-FR')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Mobile Money Tab */}
            {activeTab === 'mobile-money' && (
              <div className="space-y-6">
                {/* Stats summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className={`p-4 rounded-xl ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'} shadow-lg`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Smartphone className="w-5 h-5 text-[#5B2D8E]" />
                      <span className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>Total transactions</span>
                    </div>
                    <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {mobileMoneyTransactions.length}
                    </p>
                  </div>
                  <div className={`p-4 rounded-xl ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'} shadow-lg`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-5 h-5 text-[#F7C948]" />
                      <span className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>En attente</span>
                    </div>
                    <p className="text-2xl font-bold text-[#F7C948]">
                      {mobileMoneyTransactions.filter(t => t.status === 'pending').length}
                    </p>
                  </div>
                  <div className={`p-4 rounded-xl ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'} shadow-lg`}>
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-5 h-5 text-[#0D9488]" />
                      <span className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>Confirmés</span>
                    </div>
                    <p className="text-2xl font-bold text-[#0D9488]">
                      {mobileMoneyTransactions.filter(t => t.status === 'completed').length}
                    </p>
                  </div>
                  <div className={`p-4 rounded-xl ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'} shadow-lg`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Wallet className="w-5 h-5 text-[#5B2D8E]" />
                      <span className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>Montant total</span>
                    </div>
                    <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {new Intl.NumberFormat('fr-FR').format(
                        mobileMoneyTransactions
                          .filter(t => t.status === 'completed')
                          .reduce((sum, t) => sum + (t.amount || 0), 0)
                      )} <span className="text-sm font-normal">FCFA</span>
                    </p>
                  </div>
                </div>

                {/* Filters */}
                <div className={`p-4 rounded-xl ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'} shadow-lg`}>
                  <div className="flex flex-wrap items-center gap-4">
                    <select
                      value={mmStatusFilter}
                      onChange={(e) => setMmStatusFilter(e.target.value)}
                      className={`px-4 py-2 rounded-lg border ${
                        isDark 
                          ? 'bg-white/5 border-white/10 text-white' 
                          : 'bg-gray-50 border-gray-200 text-gray-900'
                      } outline-none`}
                    >
                      <option value="all">Tous les statuts</option>
                      <option value="pending">En attente</option>
                      <option value="processing">En cours</option>
                      <option value="completed">Confirmés</option>
                      <option value="failed">Échoués</option>
                    </select>

                    <select
                      value={mmProviderFilter}
                      onChange={(e) => setMmProviderFilter(e.target.value)}
                      className={`px-4 py-2 rounded-lg border ${
                        isDark 
                          ? 'bg-white/5 border-white/10 text-white' 
                          : 'bg-gray-50 border-gray-200 text-gray-900'
                      } outline-none`}
                    >
                      <option value="all">Tous les opérateurs</option>
                      <option value="orange">Orange Money</option>
                      <option value="mtn">MTN MoMo</option>
                      <option value="wave">Wave</option>
                      <option value="moov">Moov Money</option>
                    </select>

                    <span className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                      {mobileMoneyTransactions.filter(t => 
                        (mmStatusFilter === 'all' || t.status === mmStatusFilter) &&
                        (mmProviderFilter === 'all' || t.provider === mmProviderFilter)
                      ).length} transaction(s)
                    </span>

                    <div className="flex-1" />
                    
                    <button
                      onClick={exportMobileMoneyCSV}
                      className="flex items-center gap-2 px-4 py-2 bg-[#0D9488] text-white rounded-lg hover:bg-[#0b7c72] transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Exporter CSV
                    </button>
                  </div>
                </div>

                {/* Transactions Table */}
                <div className={`rounded-xl overflow-hidden ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'} shadow-lg`}>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className={isDark ? 'bg-white/5' : 'bg-gray-50'}>
                          <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                            Référence
                          </th>
                          <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                            Donateur
                          </th>
                          <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                            Opérateur
                          </th>
                          <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                            Montant
                          </th>
                          <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                            Statut
                          </th>
                          <th className={`px-4 py-3 text-left text-sm font-semibold ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                            Date
                          </th>
                          <th className={`px-4 py-3 text-right text-sm font-semibold ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {mobileMoneyTransactions
                          .filter(t => 
                            (mmStatusFilter === 'all' || t.status === mmStatusFilter) &&
                            (mmProviderFilter === 'all' || t.provider === mmProviderFilter)
                          )
                          .map((transaction, index) => {
                            const providerColors = {
                              orange: '#FF6600',
                              mtn: '#FFCC00',
                              wave: '#1DC3F5',
                              moov: '#0066CC'
                            };
                            const providerNames = {
                              orange: 'Orange Money',
                              mtn: 'MTN MoMo',
                              wave: 'Wave',
                              moov: 'Moov Money'
                            };
                            const statusConfig = {
                              pending: { label: 'En attente', color: '#F7C948', icon: Clock },
                              processing: { label: 'En cours', color: '#3B82F6', icon: RefreshCw },
                              completed: { label: 'Confirmé', color: '#0D9488', icon: CheckCircle2 },
                              failed: { label: 'Échoué', color: '#EF4444', icon: XCircle }
                            };
                            const status = statusConfig[transaction.status] || statusConfig.pending;
                            const StatusIcon = status.icon;
                            
                            return (
                              <tr 
                                key={transaction.id || index}
                                className={`border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`}
                              >
                                <td className={`px-4 py-3 font-mono text-sm ${isDark ? 'text-[#F7C948]' : 'text-[#5B2D8E]'}`}>
                                  {transaction.reference}
                                </td>
                                <td className="px-4 py-3">
                                  <div>
                                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                      {transaction.donor_name || 'Anonyme'}
                                    </p>
                                    <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                                      {transaction.phone_number}
                                    </p>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <span 
                                    className="px-2 py-1 rounded-lg text-xs font-medium"
                                    style={{ 
                                      backgroundColor: `${providerColors[transaction.provider]}20`,
                                      color: providerColors[transaction.provider]
                                    }}
                                  >
                                    {providerNames[transaction.provider] || transaction.provider}
                                  </span>
                                </td>
                                <td className={`px-4 py-3 font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                  {new Intl.NumberFormat('fr-FR').format(transaction.amount)} FCFA
                                </td>
                                <td className="px-4 py-3">
                                  <span 
                                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium w-fit"
                                    style={{ 
                                      backgroundColor: `${status.color}20`,
                                      color: status.color
                                    }}
                                  >
                                    <StatusIcon className="w-3 h-3" />
                                    {status.label}
                                  </span>
                                </td>
                                <td className={`px-4 py-3 text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                                  {new Date(transaction.created_at).toLocaleDateString('fr-FR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  {transaction.status === 'pending' && (
                                    <button
                                      onClick={() => setConfirmingTransaction(transaction)}
                                      className="px-3 py-1.5 bg-[#0D9488] text-white text-xs font-medium rounded-lg hover:bg-[#0b7c72] transition-colors"
                                    >
                                      Confirmer
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                    
                    {mobileMoneyTransactions.length === 0 && (
                      <div className={`p-8 text-center ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                        <Smartphone className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>Aucune transaction Mobile Money</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Confirmation Modal */}
      <AnimatePresence>
        {confirmingTransaction && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setConfirmingTransaction(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-md p-6 rounded-xl ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'} shadow-2xl`}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#0D9488]/20 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-[#0D9488]" />
                </div>
                <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Confirmer le paiement ?
                </h3>
                <p className={`mt-2 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  Vérifiez que le paiement a bien été reçu avant de confirmer.
                </p>
              </div>

              <div className={`p-4 rounded-lg mb-6 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className={`${isDark ? 'text-white/50' : 'text-gray-500'}`}>Référence</span>
                    <p className={`font-mono font-bold ${isDark ? 'text-[#F7C948]' : 'text-[#5B2D8E]'}`}>
                      {confirmingTransaction.reference}
                    </p>
                  </div>
                  <div>
                    <span className={`${isDark ? 'text-white/50' : 'text-gray-500'}`}>Montant</span>
                    <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {new Intl.NumberFormat('fr-FR').format(confirmingTransaction.amount)} FCFA
                    </p>
                  </div>
                  <div>
                    <span className={`${isDark ? 'text-white/50' : 'text-gray-500'}`}>Donateur</span>
                    <p className={isDark ? 'text-white' : 'text-gray-900'}>
                      {confirmingTransaction.donor_name || 'Anonyme'}
                    </p>
                  </div>
                  <div>
                    <span className={`${isDark ? 'text-white/50' : 'text-gray-500'}`}>Téléphone</span>
                    <p className={isDark ? 'text-white' : 'text-gray-900'}>
                      {confirmingTransaction.phone_number}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmingTransaction(null)}
                  className={`flex-1 py-3 rounded-lg font-medium transition-colors ${
                    isDark 
                      ? 'bg-white/10 text-white hover:bg-white/20' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Annuler
                </button>
                <button
                  onClick={() => confirmMobileMoneyPayment(confirmingTransaction.reference)}
                  className="flex-1 py-3 bg-[#0D9488] text-white font-bold rounded-lg hover:bg-[#0b7c72] transition-colors"
                >
                  Confirmer le paiement
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Member Detail Modal */}
      <AnimatePresence>
        {selectedMember && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setSelectedMember(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-lg rounded-xl overflow-hidden ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'} shadow-2xl`}
            >
              {/* Header */}
              <div className="p-6 bg-gradient-to-r from-[#5B2D8E] to-[#7B4AAE]">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-white text-xl font-bold">
                    {selectedMember.firstName?.[0]}{selectedMember.lastName?.[0]}
                  </div>
                  <div className="text-white">
                    <h3 className="text-xl font-bold">
                      {selectedMember.firstName} {selectedMember.lastName}
                    </h3>
                    <p className="opacity-80">{pathLabels[selectedMember.path]?.label}</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className={`text-xs font-medium mb-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Email</p>
                    <p className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedMember.email}</p>
                  </div>
                  <div>
                    <p className={`text-xs font-medium mb-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Téléphone</p>
                    <p className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedMember.phone || '-'}</p>
                  </div>
                  <div>
                    <p className={`text-xs font-medium mb-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Ville</p>
                    <p className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedMember.city}</p>
                  </div>
                  <div>
                    <p className={`text-xs font-medium mb-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Profession</p>
                    <p className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedMember.profession || '-'}</p>
                  </div>
                </div>

                {selectedMember.motivation && (
                  <div>
                    <p className={`text-xs font-medium mb-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Motivation</p>
                    <p className={`text-sm ${isDark ? 'text-white/80' : 'text-gray-700'}`}>{selectedMember.motivation}</p>
                  </div>
                )}

                {selectedMember.skills && (
                  <div>
                    <p className={`text-xs font-medium mb-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Compétences</p>
                    <p className={`text-sm ${isDark ? 'text-white/80' : 'text-gray-700'}`}>{selectedMember.skills}</p>
                  </div>
                )}

                <div className="pt-4 border-t border-white/10 flex items-center justify-between">
                  <div>
                    <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                      Inscrit le {new Date(selectedMember.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <select
                    value={selectedMember.status}
                    onChange={(e) => updateMemberStatus(selectedMember.id, e.target.value)}
                    className={`px-3 py-2 rounded-lg border ${
                      isDark ? 'bg-white/10 border-white/10' : 'bg-gray-100 border-gray-200'
                    }`}
                    style={{ color: statusLabels[selectedMember.status]?.color }}
                  >
                    {Object.entries(statusLabels).map(([key, { label }]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPage;
